// src/pages/KeywordGridPage.js
import { AbsoluteCenter, Center } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import RegularSolidButton from '../../components/buttons/RegularSolidButton';
import { MBTICard } from '../../components/fan-buddy/MBTICard';
import StickyHeader from '../../components/StickyHeader';
import { ReactComponent as CloseIcon } from "../../icons/xmark.svg";
import { FAN_BUDDY_MBTI, getCompatibleMBTI, getIncompatibleMBTI } from '../../utils/FanBuddyHelper';
import { isDarkMode } from '../../utils/ThemeHelper';
import { t } from 'i18next';

const Title = styled.div`
  font-size: 26px;
  font-weight: 700;
  line-height: 31px;
  text-align: center;
  white-space: pre-wrap;
  text-align: left;
`;

const MBTICardContainer = styled.div`
  margin: 20px 0 12px 0;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  &::before {
    content: "";
    position: absolute;
    inset: -10px; // 라이트/다크 동일
    border-radius: 24px;
    pointer-events: none;
    z-index: 0;
    /* filter, opacity를 라이트모드와 동일하게 고정 */
    opacity: 0.9;
    filter: blur(28px);
    background: ${({ isDarkMode }) =>
      isDarkMode
        ? `
          linear-gradient(135deg,
            rgba(203,227,255,0) 0%,
            rgba(0,0,0,0.7) 60%,
            rgba(151,255,198,0.64) 100%
          ),
          linear-gradient(135deg,
            rgba(0,0,0,0.7) 0%,
            rgba(0,0,0,1) 6%,
            rgba(0,0,0,0.7) 20%,
            rgba(0,0,0,1) 36%,
            rgba(0,0,0,0.48) 70%,
            rgba(0,0,0,1) 100%
          )
        `
        : `
          linear-gradient(135deg,
            rgba(203,227,255,0) 0%,
            rgba(255,255,255,0.7) 60%,
            rgba(151,255,198,0.64) 100%
          ),
          linear-gradient(135deg,
            rgba(255,255,255,0.7) 0%,
            rgba(255,255,255,1) 6%,
            rgba(255,255,255,0.3) 20%,
            rgba(255,255,255,1) 36%,
            rgba(255,255,255,0.48) 70%,
            rgba(255,255,255,1) 100%
          )
        `};
    mix-blend-mode: lighten;
  }
  & > * {
    position: relative;
    z-index: 1;
  }
`;

const VerticalDivider = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 1px;
  height: 100%;
  background: var(--separator-lv2, #E7E7E7);
  @media (prefers-color-scheme: dark) {
    background: var(--separator-lv1, #303030);
  }
`;

const CompatibleMBTITitle = styled.div`
  font-size: 12px;
  font-weight: 500;
  line-height: 15px;
  text-align: center;
  padding-bottom: 10px;
`;

const CompatibleMBTIName = styled.div`
  font-size: 13px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
  background: ${(props) =>
    `linear-gradient(90deg, rgba(${props.textPrimaryColor},1) 0%, rgba(${props.textSecondaryColor},1) 100%)`};
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
`;


const ShowFanBuddyCardPage = () => {
  const navigate = useNavigate();

  const currentQueryParams = new URLSearchParams(window.location.search);
  const mbti = currentQueryParams.get("mbti");
  const showFindButton = currentQueryParams.get("showFindButton");
  const returnToFeed = currentQueryParams.get("returnToFeed");

  const onClickReStart = () => {
    navigate('/fan-buddy/intro');
  };

  const exit = () => {
    if(returnToFeed) {
      navigate('/home', { replace: true });
    } else {
      navigate(-1)
    }
  }

  return (
    <div className="min-h-screen bg-white dark:bg-[#222222]">
      <StickyHeader
        showBackButton={false}
        transparent={true}
        leftContent={
          <CloseIcon
            className="fill-pure-black dark:fill-pure-white"
            width={'20px'}
            height={'20px'}
            onClick={exit}
          />
        }
        style={{ maxWidth: 'calc(var(--chakra-sizes-xl))' }}
      />
      <AbsoluteCenter
        style={{
          marginTop: 'calc(24px + env(safe-area-inset-top))',
          padding: '0 24px',
          width: '100%',
          maxWidth: 'calc(var(--chakra-sizes-xl))'
        }}
      >
        <Center flexDirection={'column'}>
          <Title>
            {showFindButton
              ? t('ShowFanBuddyCard.completeTitle')
              : t('ShowFanBuddyCard.myFanBuddyCard')}
          </Title>
          <div className="w-full text-[15px] font-normal leading-[18px] text-center pt-[14px] mb-[16px] text-main-light-4 dark:text-main-dark-4">
            {t('ShowFanBuddyCard.description')}
          </div>
          <MBTICardContainer>
            <MBTICard mbti={mbti} isDarkMode={isDarkMode()} />
          </MBTICardContainer>
          <div className="mt-[16px] border-[2px] rounded-[12px] w-full text-left py-[12px] px-[18px] border-separator-light-1 dark:border-separator-dark-1">
            <div className="text-[13px] font-semibold leading-[22px] text-main-light-3 dark:text-main-dark-3 mb-[1px]">
              {t('ShowFanBuddyCard.likeYourMBTI', { mbti: FAN_BUDDY_MBTI[mbti].name })}
            </div>
            {FAN_BUDDY_MBTI[mbti]['descriptions']?.map((description) => (
              <div className="flex flex-row gap-[6px] items-center" style={{ lineHeight: '20px' }}>
                <span className="text-[#666666] dark:text-[#AAABAC]">•</span>
                <div className="text-[13px] font-normal leading-[16px] text-main-light-3 dark:text-main-dark-3">
                  {description}
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-between w-full mt-[16px]">
            <div className="flex-1 text-center">
              <CompatibleMBTITitle>{t('ShowFanBuddyCard.goodMatch')}</CompatibleMBTITitle>
              <div className="flex flex-col gap-[3px]">
                {getCompatibleMBTI(mbti).map((compatibleMBTI) => (
                  <CompatibleMBTIName
                    key={compatibleMBTI}
                    textPrimaryColor={FAN_BUDDY_MBTI[compatibleMBTI]['textColor']['primary']}
                    textSecondaryColor={FAN_BUDDY_MBTI[compatibleMBTI]['textColor']['secondary']}
                  >
                    {FAN_BUDDY_MBTI[compatibleMBTI]['name']}
                  </CompatibleMBTIName>
                ))}
              </div>
            </div>
            {/* 세로보더선 */}
            <div className="flex-1 text-center relative" style={{ height: '100%' }}>
              <VerticalDivider />
              <CompatibleMBTITitle>{t('ShowFanBuddyCard.badMatch')}</CompatibleMBTITitle>
              <div className="flex flex-col gap-[3px]">
                {getIncompatibleMBTI(mbti).map((incompatibleMBTI) => (
                  <CompatibleMBTIName
                    key={incompatibleMBTI}
                    textPrimaryColor={FAN_BUDDY_MBTI[incompatibleMBTI]['textColor']['primary']}
                    textSecondaryColor={FAN_BUDDY_MBTI[incompatibleMBTI]['textColor']['secondary']}
                  >
                    {FAN_BUDDY_MBTI[incompatibleMBTI]['name']}
                  </CompatibleMBTIName>
                ))}
              </div>
            </div>
          </div>
          {showFindButton && (
            <RegularSolidButton
              text={t('ShowFanBuddyCard.startButton')}
              style={{
                marginTop: '24px',
                padding: '16px 20px',
                width: '100%',
                borderRadius: '8px'
              }}
              onClicked={() => navigate('/fan-buddy/find')}
            />
          )}
          <div
            className="
            text-[13px] font-normal leading-[16px] text-center underline
            text-main-light-3 dark:text-main-dark-3
            mt-[24px] safeAreaMarginBottom
            "
            onClick={() => onClickReStart()}
          >
            {t('ShowFanBuddyCard.restartButton')}
          </div>
        </Center>
      </AbsoluteCenter>
    </div>
  );
};

export default ShowFanBuddyCardPage;
